import { useState } from 'react';
import { Button } from './ui/Button';
import { Input } from './ui/Input';
import { Card, CardContent } from './ui/Card';
import { Badge } from './ui/Badge';
import { CheckCircle, XCircle, AlertCircle, SkipForward, Edit2, Save, X, Download } from 'lucide-react';

export default function ImportPreview({ rows, onRowsChange, onImport, importType = 'clients' }) {
  const [editingRow, setEditingRow] = useState(null);
  const [editedData, setEditedData] = useState(null);

  const handleRowAction = (rowIndex, action) => {
    const updated = rows.map((row, idx) => 
      idx === rowIndex 
        ? { ...row, action: action === row.action ? null : action }
        : row
    );
    onRowsChange(updated);
  };

  const handleBulkAction = (filterFn, action) => {
    const updated = rows.map(row => 
      filterFn(row) ? { ...row, action } : row
    );
    onRowsChange(updated);
  };

  const startEdit = (rowIndex) => {
    const row = rows[rowIndex];
    setEditingRow(rowIndex);
    setEditedData({ ...row.data });
  };

  const saveEdit = (rowIndex) => {
    const updated = rows.map((row, idx) => 
      idx === rowIndex 
        ? { ...row, data: editedData, action: 'import' }
        : row
    );
    onRowsChange(updated);
    setEditingRow(null);
    setEditedData(null);
  };

  const cancelEdit = () => {
    setEditingRow(null);
    setEditedData(null);
  };

  const getRowStatus = (row) => {
    if (row.action === 'skip') return { icon: SkipForward, color: 'text-[var(--ds-text-muted)]', label: 'Skipped' };
    if (row.duplicate?.isDuplicate) return { icon: XCircle, color: 'text-[var(--ds-status-danger)]', label: 'Duplicate' };
    if (!row.validation?.isValid) return { icon: AlertCircle, color: 'text-[var(--ds-status-warning)]', label: 'Errors' };
    if (row.action === 'import') return { icon: CheckCircle, color: 'text-[var(--ds-status-success)]', label: 'Ready' };
    return { icon: CheckCircle, color: 'text-[var(--ds-text-muted)]', label: 'Valid' };
  };

  const summary = {
    total: rows.length,
    valid: rows.filter(r => {
      const isValid = r.validation?.isValid && !r.duplicate?.isDuplicate;
      const hasClientMatch = importType === 'client-contacts' ? r.clientMatch?.found : true;
      return isValid && hasClientMatch;
    }).length,
    errors: rows.filter(r => !r.validation?.isValid).length,
    duplicates: rows.filter(r => r.duplicate?.isDuplicate).length,
    toImport: rows.filter(r => r.action === 'import').length,
    toSkip: rows.filter(r => r.action === 'skip').length,
  };

  const exportErrorReport = () => {
    const errorRows = rows.filter(r => !r.validation?.isValid || r.duplicate?.isDuplicate);
    const csv = [
      ['Row', 'Status', 'Errors', 'Data'].join(','),
      ...errorRows.map(r => [
        r.rowIndex + 1,
        r.duplicate?.isDuplicate ? 'Duplicate' : 'Validation Error',
        r.validation?.errors?.join('; ') || '',
        JSON.stringify(r.data)
      ].join(','))
    ].join('\n');
    
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'import-errors.csv';
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="space-y-6">
      {/* Summary Stats */}
      <Card>
        <CardContent className="p-6">
          <div className={`grid grid-cols-2 ${importType === 'client-contacts' ? 'md:grid-cols-7' : 'md:grid-cols-6'} gap-4`}>
            <div>
              <div className="text-micro text-[var(--ds-text-muted)] mb-1">Total Rows</div>
              <div className="text-h2 font-semibold text-[var(--ds-text-primary)]">{summary.total}</div>
            </div>
            <div>
              <div className="text-micro text-[var(--ds-text-muted)] mb-1">Valid</div>
              <div className="text-h2 font-semibold text-[var(--ds-status-success)]">{summary.valid}</div>
            </div>
            <div>
              <div className="text-micro text-[var(--ds-text-muted)] mb-1">Errors</div>
              <div className="text-h2 font-semibold text-[var(--ds-status-warning)]">{summary.errors}</div>
            </div>
            <div>
              <div className="text-micro text-[var(--ds-text-muted)] mb-1">Duplicates</div>
              <div className="text-h2 font-semibold text-[var(--ds-status-danger)]">{summary.duplicates}</div>
            </div>
            {importType === 'client-contacts' && (
              <div>
                <div className="text-micro text-[var(--ds-text-muted)] mb-1">Unmatched</div>
                <div className="text-h2 font-semibold text-[var(--ds-status-danger)]">
                  {rows.filter(r => !r.clientMatch?.found).length}
                </div>
              </div>
            )}
            <div>
              <div className="text-micro text-[var(--ds-text-muted)] mb-1">To Import</div>
              <div className="text-h2 font-semibold text-[var(--ds-accent-primary)]">{summary.toImport}</div>
            </div>
            <div>
              <div className="text-micro text-[var(--ds-text-muted)] mb-1">To Skip</div>
              <div className="text-h2 font-semibold text-[var(--ds-text-muted)]">{summary.toSkip}</div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Bulk Actions */}
      <div className="flex flex-wrap gap-2">
        <Button
          variant="outline"
          size="sm"
          onClick={() => handleBulkAction(r => r.validation?.isValid && !r.duplicate?.isDuplicate, 'import')}
        >
          Import All Valid
        </Button>
        <Button
          variant="outline"
          size="sm"
          onClick={() => handleBulkAction(r => r.duplicate?.isDuplicate, 'skip')}
        >
          Skip All Duplicates
        </Button>
        <Button
          variant="outline"
          size="sm"
          onClick={() => handleBulkAction(r => !r.validation?.isValid, 'skip')}
        >
          Skip All Errors
        </Button>
        {importType === 'client-contacts' && (
          <Button
            variant="outline"
            size="sm"
            onClick={() => handleBulkAction(r => !r.clientMatch?.found, 'skip')}
          >
            Skip All Unmatched
          </Button>
        )}
        {(summary.errors > 0 || summary.duplicates > 0) && (
          <Button
            variant="outline"
            size="sm"
            onClick={exportErrorReport}
            className="flex items-center gap-2"
          >
            <Download className="h-4 w-4" />
            Export Error Report
          </Button>
        )}
      </div>

      {/* Preview Table */}
      <Card>
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-[var(--ds-surface-panel2)] border-b border-[var(--ds-border-divider)]">
                <tr>
                  <th className="px-4 py-3 text-left text-micro font-medium text-[var(--ds-text-secondary)]">Status</th>
                  {importType === 'client-contacts' && (
                    <th className="px-4 py-3 text-left text-micro font-medium text-[var(--ds-text-secondary)]">Client Match</th>
                  )}
                  {importType === 'clients' && (
                    <th className="px-4 py-3 text-left text-micro font-medium text-[var(--ds-text-secondary)]">Type</th>
                  )}
                  {importType === 'suppliers' && (
                    <th className="px-4 py-3 text-left text-micro font-medium text-[var(--ds-text-secondary)]">Supplier Code</th>
                  )}
                  <th className="px-4 py-3 text-left text-micro font-medium text-[var(--ds-text-secondary)]">
                    {importType === 'client-contacts' ? 'Contact Name' : importType === 'suppliers' ? 'Supplier Name' : 'Name/Company'}
                  </th>
                  <th className="px-4 py-3 text-left text-micro font-medium text-[var(--ds-text-secondary)]">Email</th>
                  <th className="px-4 py-3 text-left text-micro font-medium text-[var(--ds-text-secondary)]">Phone</th>
                  {importType === 'client-contacts' && (
                    <th className="px-4 py-3 text-left text-micro font-medium text-[var(--ds-text-secondary)]">Position</th>
                  )}
                  <th className="px-4 py-3 text-left text-micro font-medium text-[var(--ds-text-secondary)]">Issues</th>
                  <th className="px-4 py-3 text-left text-micro font-medium text-[var(--ds-text-secondary)]">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-[var(--ds-border-divider)]">
                {rows.map((row, index) => {
                  const status = getRowStatus(row);
                  const StatusIcon = status.icon;
                  const isEditing = editingRow === index;
                  const data = isEditing ? editedData : row.data;
                  const displayName = importType === 'client-contacts'
                    ? `${data.first_name || ''} ${data.last_name || ''}`.trim() || '—'
                    : importType === 'suppliers'
                    ? data.supplier_name || '—'
                    : row.clientType === 'company' 
                      ? data.company_name || '—'
                      : `${data.first_name || ''} ${data.last_name || ''}`.trim() || '—';

                  return (
                    <tr
                      key={index}
                      className={`hover:bg-[var(--ds-surface-panel2)] ${
                        row.action === 'import' ? 'bg-[var(--ds-status-success-bg)]/20' :
                        row.action === 'skip' ? 'bg-[var(--ds-surface-panel2)] opacity-60' :
                        ''
                      }`}
                    >
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-2">
                          <StatusIcon className={`h-4 w-4 ${status.color}`} />
                          <span className="text-micro text-[var(--ds-text-secondary)]">{status.label}</span>
                        </div>
                      </td>
                      {importType === 'client-contacts' && (
                        <td className="px-4 py-3">
                          {row.clientMatch?.found ? (
                            <div className="flex items-center gap-2">
                              <CheckCircle className="h-4 w-4 text-[var(--ds-status-success)]" />
                              <span className="text-body text-[var(--ds-text-primary)]">
                                {row.clientMatch.clientName || '—'}
                              </span>
                            </div>
                          ) : (
                            <div className="flex items-center gap-2">
                              <XCircle className="h-4 w-4 text-[var(--ds-status-danger)]" />
                              <span className="text-body text-[var(--ds-status-danger)]">
                                No match found
                              </span>
                            </div>
                          )}
                        </td>
                      )}
                      {importType === 'clients' && (
                        <td className="px-4 py-3">
                          <Badge variant="neutral">
                            {row.clientType === 'company' ? 'Company' : 'Individual'}
                          </Badge>
                        </td>
                      )}
                      {importType === 'suppliers' && (
                        <td className="px-4 py-3">
                          <span className="text-body text-[var(--ds-text-primary)]">{data.supplier_code || '—'}</span>
                        </td>
                      )}
                      <td className="px-4 py-3">
                        {isEditing ? (
                          <Input
                            value={importType === 'client-contacts' 
                              ? (data.first_name || '')
                              : importType === 'suppliers'
                              ? (data.supplier_name || '')
                              : row.clientType === 'company' 
                                ? (data.company_name || '') 
                                : (data.first_name || '')}
                            onChange={(e) => {
                              if (importType === 'client-contacts') {
                                setEditedData({ ...data, first_name: e.target.value });
                              } else if (importType === 'suppliers') {
                                setEditedData({ ...data, supplier_name: e.target.value });
                              } else if (row.clientType === 'company') {
                                setEditedData({ ...data, company_name: e.target.value });
                              } else {
                                setEditedData({ ...data, first_name: e.target.value });
                              }
                            }}
                            className="h-8 w-full border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                          />
                        ) : (
                          <span className="text-body text-[var(--ds-text-primary)]">{displayName}</span>
                        )}
                      </td>
                      <td className="px-4 py-3">
                        {isEditing ? (
                          <Input
                            type="email"
                            value={data.email || ''}
                            onChange={(e) => setEditedData({ ...data, email: e.target.value })}
                            className="h-8 w-full border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                          />
                        ) : (
                          <span className="text-body text-[var(--ds-text-primary)]">{data.email || '—'}</span>
                        )}
                      </td>
                      <td className="px-4 py-3">
                        {isEditing ? (
                          <Input
                            value={data.phone || ''}
                            onChange={(e) => setEditedData({ ...data, phone: e.target.value })}
                            className="h-8 w-full border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                          />
                        ) : (
                          <span className="text-body text-[var(--ds-text-primary)]">{data.phone || '—'}</span>
                        )}
                      </td>
                      {importType === 'client-contacts' && (
                        <td className="px-4 py-3">
                          {isEditing ? (
                            <Input
                              value={data.position || ''}
                              onChange={(e) => setEditedData({ ...data, position: e.target.value })}
                              className="h-8 w-full border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                            />
                          ) : (
                            <span className="text-body text-[var(--ds-text-primary)]">{data.position || '—'}</span>
                          )}
                        </td>
                      )}
                      <td className="px-4 py-3">
                        <div className="space-y-1">
                          {row.validation?.errors?.map((error, i) => (
                            <div key={i} className="text-micro text-[var(--ds-status-danger)]">
                              {error}
                            </div>
                          ))}
                          {row.validation?.warnings?.map((warning, i) => (
                            <div key={i} className="text-micro text-[var(--ds-status-warning)]">
                              {warning}
                            </div>
                          ))}
                          {row.duplicate?.isDuplicate && (
                            <div className="text-micro text-[var(--ds-status-danger)]">
                              Duplicate email
                            </div>
                          )}
                          {!row.validation?.errors?.length && !row.validation?.warnings?.length && !row.duplicate?.isDuplicate && (
                            <span className="text-micro text-[var(--ds-text-muted)]">—</span>
                          )}
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-2">
                          {isEditing ? (
                            <>
                              <Button
                                variant="primary"
                                size="sm"
                                onClick={() => saveEdit(index)}
                              >
                                <Save className="h-3 w-3" />
                              </Button>
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={cancelEdit}
                              >
                                <X className="h-3 w-3" />
                              </Button>
                            </>
                          ) : (
                            <>
                              {row.action !== 'import' && (
                                <Button
                                  variant="primary"
                                  size="sm"
                                  onClick={() => handleRowAction(index, 'import')}
                                  disabled={!row.validation?.isValid || row.duplicate?.isDuplicate || (importType === 'client-contacts' && !row.clientMatch?.found)}
                                >
                                  Import
                                </Button>
                              )}
                              {row.action !== 'skip' && (
                                <Button
                                  variant="outline"
                                  size="sm"
                                  onClick={() => handleRowAction(index, 'skip')}
                                >
                                  Skip
                                </Button>
                              )}
                              {!row.validation?.isValid && (
                                <Button
                                  variant="outline"
                                  size="sm"
                                  onClick={() => startEdit(index)}
                                >
                                  <Edit2 className="h-3 w-3" />
                                </Button>
                              )}
                            </>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>

      {/* Import Button */}
      {summary.toImport > 0 && (
        <div className="flex justify-end">
          <Button
            variant="primary"
            onClick={() => onImport(rows.filter(r => r.action === 'import'))}
            className="px-6"
          >
            Import {summary.toImport} {summary.toImport === 1 ? 'Client' : 'Clients'}
          </Button>
        </div>
      )}
    </div>
  );
}

